import { getFileContentFromGithub } from "../../Api/ServerInterface";
import useStore from "../../Store/Store";
import { openFile } from "../../utils/LocalFileController/LocalFileController";
import { logger } from "../../utils/Logger";
import { findFileInRepoData } from "../OnboardingWorkflow/OnboardingWorkflow";

export async function getFileContent(path = "") {
  const sessionMode = useStore.getState().session.mode;
  let currentPath = path || useStore.getState().currentPath;
  const currentBranch = useStore.getState().currentBranch;
  const repoData = useStore.getState().repoData;
  const setFileContent = useStore.getState().setFileContent;

  // Check if the path exists in repoData
  if (!repoData[currentPath]) {
    logger.debug.warn(`Path not found in repoData: ${currentPath}`);

    // Try to find the actual file using the existing path resolution logic
    const resolvedPath = findFileInRepoData(currentPath, repoData);
    if (resolvedPath) {
      logger.debug.info(
        `Resolved simulation path '${currentPath}' to actual path '${resolvedPath}'`
      );
      currentPath = resolvedPath;
    } else {
      // If we still can't find the file, return placeholder content
      // This handles cases like Next.js dynamic routes [username]/[repo] that don't exist in actual repo
      const placeholderContent = `// File not found in repository: ${currentPath}\n// This might be a dynamic route or auto-generated file pattern.`;
      setFileContent(currentPath, placeholderContent);
      return placeholderContent;
    }
  }

  let currentRepoData = repoData[currentPath];

  if (currentRepoData?.parentPath) {
    currentPath = currentRepoData.parentPath;
    currentRepoData = repoData[currentPath];
  }

  let content;

  // Check documentMap for cached content
  const documentMap = useStore.getState().documentMap;
  let documentMapContent;
  if (documentMap) {
    for (const [key, value] of documentMap) {
      if (value.path === currentPath) {
        documentMapContent = value.content;
        logger.debug.info(
          `Found cached content in documentMap for: ${currentPath}`
        );
        break;
      }
    }
  }

  // First check if file content is already available (for GitHub mode where entire repo is downloaded)
  // Check documentMap first, then 'fileContent' (legacy/manual) and 'content' (from archive download)
  const cachedContent =
    documentMapContent ||
    repoData[currentPath]?.fileContent ||
    repoData[currentPath]?.content;

  if (cachedContent) {
    logger.debug.info(`Using cached file content for: ${currentPath}`);
    content = cachedContent;
  } else if (
    sessionMode === "github" ||
    sessionMode === "unauthenticatedGithub"
  ) {
    // If file content is not cached, check if URL exists for fetching from GitHub
    if (!repoData[currentPath]?.url) {
      logger.debug.warn(`No URL found for file: ${currentPath}`);
      // Return placeholder content instead of throwing error
      const placeholderContent = `// No URL found for file: ${currentPath}\n// This might be a dynamic route or auto-generated file pattern.\n Please reload the page.`;
      setFileContent(currentPath, placeholderContent);
      return placeholderContent;
    }
    content = await getFileContentFromGithub(
      repoData[currentPath].url,
      currentPath,
      currentBranch
    );
  } else if (sessionMode === "local") {
    const result = await openFile(currentPath);
    // Handle cases where openFile returns null/undefined or has null fileContent
    if (!result || result.fileContent === null) {
      logger.debug.warn(`Unable to read file: ${currentPath}`);
      // Return placeholder content instead of throwing error
      const placeholderContent = `// Unable to read file: ${currentPath}\n// File might not exist or be inaccessible.`;
      setFileContent(currentPath, placeholderContent);
      return placeholderContent;
    }
    content = result.fileContent;
  } else {
    throw new Error(`Unsupported session mode: ${sessionMode}`);
  }

  // handle corner case where code gets parsed if it was in json format
  if (typeof content !== "string") {
    content = JSON.stringify(content, null, 2);
  }

  // Always call setFileContent for potential side effects (metadata updates, re-renders, etc.)
  setFileContent(currentPath, content);
  return content;
}
